"use client";

import { Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: iconRef,
    isVisible: iconVisible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light/30 border border-primary/20 rounded-lg shadow-soft">
        <p className="text-primary-dark font-semibold text-lg mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="btn-primary">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div ref={formRef} className="space-y-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 50,
          message: "Name must be less than 50 characters"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <FormLabel className="text-foreground font-semibold">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your full name" className="focus-ring h-12 bg-background border-border-strong transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{
        transitionDelay: formVisible ? '80ms' : '0ms'
      }}>
              <FormLabel className="text-foreground font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="your.email@example.com" className="focus-ring h-12 bg-background border-border-strong transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\d\s\-\+\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{
        transitionDelay: formVisible ? '160ms' : '0ms'
      }}>
              <FormLabel className="text-foreground font-semibold">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(123) 456-7890" className="focus-ring h-12 bg-background border-border-strong transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{
        transitionDelay: formVisible ? '240ms' : '0ms'
      }}>
              <FormLabel className="text-foreground font-semibold">Message</FormLabel>
              <FormControl>
                <textarea placeholder="Tell us about your travel needs..." rows={5} className="w-full px-3 py-2 rounded-md border border-border-strong bg-background text-foreground shadow-xs focus-ring transition-all duration-[var(--duration-medium)] resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
            <p className="text-destructive text-sm font-medium">{submitError}</p>
          </div>}

        <div ref={iconRef} className={`flex items-center gap-6 pt-2 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${iconVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center gap-2 text-muted">
            <Phone className="size-5 text-primary" />
            <span className="text-sm font-medium">(203) 872-9965</span>
          </div>
          <div className="flex items-center gap-2 text-muted">
            <Mail className="size-5 text-primary" />
            <span className="text-sm font-medium">info@carserviceconnecticut.com</span>
          </div>
        </div>

        <Button type="submit" disabled={isSubmitting} className="w-full btn-primary h-12 text-base font-semibold transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:shadow-strong">
          {isSubmitting ? <>
              <span className="inline-block animate-spin mr-2">⏳</span>
              Sending...
            </> : 'Send Message'}
        </Button>
      </div>
    </Form>;
}
export default function ContactInformation() {
  return <section className="relative min-h-screen bg-background py-16 px-4 md:px-8 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,_hsl(210,80%,95%)_0%,_transparent_50%)] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex justify-center items-center">
          {}
          <div className="w-full max-w-2xl mx-auto">
            <div className="glass-effect p-8 md:p-10 rounded-2xl shadow-strong">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">
                Get in Touch
              </h2>
              <p className="text-muted text-base mb-8">
                Contact us for premium car service in Connecticut and beyond. We're here to make your journey exceptional.
              </p>

              <FormProvider className="space-y-6" formId="contact-form-hero" sectionName="contact-information" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2,
                maxLength: 50
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Send Message" successMessage="Thank you for contacting Car Service Connecticut! We'll get back to you shortly.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}